import type { TemplatePage } from './templates';

/**
 * Build-time switches (the pages are prerendered): `VITE_TEMPLATE_LEAD_FORM` unset = today's pages, byte for byte (the PDF button and its copy, no
 * form, no Turnstile script). The form also needs `VITE_TURNSTILE_SITE_KEY`; without it nothing renders, because the form never works without the bot check.
 */
export const LEAD_FORM_ON = import.meta.env.VITE_TEMPLATE_LEAD_FORM === 'true';
export const TURNSTILE_SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

/**
 * DRAFT copy, from Appendix A of template-lead-capture-plan-2026-10-10.md: NOT approved by Tom yet. Every word the form shows is here, so approving
 * or changing the copy touches this one object. US English only. The error messages are plain retry wording, not part of Appendix A.
 */
export const LEAD_COPY = {
  heading: 'Get the editable Google Sheet',
  emailLabel: 'Email address',
  placeholder: 'you@yourbusiness.com',
  button: 'Send me the Google Sheet',
  sending: 'Sending…',
  consent: "We'll email you the Google Sheet link and one follow-up about Scopie. Unsubscribe any time.",
  success: 'Check your inbox: the Google Sheet link is on its way. It can take a minute, and it may land in spam.',
  freeAnswer: 'Yes. Read it here, download the PDF free with no sign-up, copy it into your own documents, or get the editable Google Sheet by email.',
  errors: {
    invalidEmail: 'Enter a valid email address.',
    botCheck: 'The security check did not pass. Please try again.',
    blocked: 'The security check could not load (a blocker may be stopping it). The PDF above is free with no sign-up.',
    checkPending: 'The security check is still loading. Wait a moment, then press the button again.',
    tooMany: 'Too many tries. Please try again in a little while.',
    unavailable: 'This is unavailable right now. Please try again later.',
    generic: 'Something went wrong. Please try again in a moment.',
  },
} as const;

/** "Is the template free?", "Is this concrete estimate template free?", "Is this flooring estimate template free to use?" */
const isFreeQuestion = (q: string) => /\bfree( to use)?\?$/i.test(q.trim());

/**
 * The page's copy when the Google Sheet form is on. The PDF and its "No sign-up" wording stay. The title and description mention the Google Sheet only
 * when the new wording fits the 60/155 limits, otherwise they are left exactly as they were; the only other change is the "Is the template free?"
 * answer (and its twin in the FAQ block that the JSON-LD reads); a page with no such question has no FAQ change. Pure: `on` false, or a page
 * without a sheet, returns the page itself.
 */
export function withLeadCopy(p: TemplatePage, on: boolean): TemplatePage {
  if (!on || !p.leadSheet) return p;
  const titled = p.title.replace(/\(PDF \+ [^)]*\)/, '(PDF + Google Sheet)');
  const title = titled !== p.title && titled.length <= 60 ? titled : p.title;
  const described = `${p.metaDescription.replace(/\s+$/, '')} Free PDF and Google Sheet.`;
  const metaDescription = described.length <= 155 ? described : p.metaDescription;
  return {
    ...p,
    title,
    metaDescription,
    faq: p.faq.map((f) => (isFreeQuestion(f.q) ? { ...f, a: LEAD_COPY.freeAnswer } : f)),
    blocks: p.blocks.map((b) => (b.type === 'faq' ? { ...b, items: b.items.map((i) => (isFreeQuestion(i.q) ? { ...i, a: LEAD_COPY.freeAnswer } : i)) } : b)),
  };
}
